import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getActiveCustomerCart } from "../utils/getCart"

const commerceApi = CommerceService.api()

export const mutationTransferOrderAddressToCart = {
  async transferOrderAddressToCart(_parent, args, context, _info) {
    let { customerId, customerNumber, orderNumber } = args

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    if (!customerId || !orderNumber) {
      throw new Error("Missing data")
    }

    const customer = await getCustomer({ id: customerId, number: customerNumber })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const customerCarts = await getActiveCustomerCart(customer.id, user.employeeNumber)
    let customerCart = customerCarts[0]

    if (!customerCart || customerCart.cartState !== "Active") {
      throw new Error("Customer does not have an active cart")
    }

    const orderShippingAddress = await getOrderShippingAddress(orderNumber, customer.customerNumber)

    if (!orderShippingAddress) {
      throw new Error("Order shipping address not found")
    }

    const addressAction = {
      action: "setShippingAddress",
      address: {
        firstName: orderShippingAddress.firstName,
        lastName: orderShippingAddress.lastName,
        streetName: orderShippingAddress.streetName,
        additionalStreetInfo: orderShippingAddress.additionalStreetInfo,
        city: orderShippingAddress.city,
        postalCode: orderShippingAddress.postalCode,
        country: orderShippingAddress.country,
      },
    }

    const payload = {
      version: customerCart.version,
      actions: [addressAction],
      storeKey: customerCart?.store?.key,
    }

    const updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: customerCart.id })
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    )?.body

    return updatedCart
  },
}

/**
 * Gets the shipping address from an order
 *
 * @param {string} orderNumber
 *
 * @returns {Promise<Object|null>}
 */
const getOrderShippingAddress = async (orderNumber) => {
  const graphiQLQueryObject = [
    {
      method: "findOrder",
      variables: { orderNumber },
      fields: [
        "orderNumber",
        "customerNumber",
        "shippingStreet",
        "shippingCity",
        "shippingPostalCode",
        "shippingCountry",
        "shippingAddress1",
        "shippingAddress2",
      ],
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)

  let customerOrderResponse
  try {
    const response = await EinzaGraphiQLService.getData(mappedQuery)
    customerOrderResponse = response?.data?.findOrder
  } catch (error) {
    throw new Error("Order data not found", error)
  }

  if (!customerOrderResponse) {
    return null
  }

  const orderShippingAddress = {
    firstName: customerOrderResponse.shippingAddress1,
    lastName: customerOrderResponse.shippingAddress2,
    streetName: customerOrderResponse.shippingStreet,
    additionalStreetInfo: customerOrderResponse.shippingAddress2,
    city: customerOrderResponse.shippingCity,
    postalCode: customerOrderResponse.shippingPostalCode,
    country: customerOrderResponse.shippingCountry || "DE",
  }

  return orderShippingAddress
}
